import { getActivityOptionsForUser } from '@/__fixtures__/activity-options.mock';
import { announcements as mockAnnouncements } from '@/__fixtures__/announcements.mock';
import {
  deriveMockCapabilities,
  getMockAccessToken,
  mockCredentials,
  mockTokens,
  mockUser,
  setMockAccessToken,
} from '@/__fixtures__/auth.mock';
import { mockInvoices } from '@/__fixtures__/billing.mock';
import { mockClientContacts, mockClients } from '@/__fixtures__/clients.mock';
import { mockConsultants } from '@/__fixtures__/consultants.mock';
import { documents as mockDocuments } from '@/__fixtures__/documents.mock';
import { expenses as mockExpenses } from '@/__fixtures__/expenses.mock';
import { getMissionsForUser } from '@/__fixtures__/missions.mock';
import {
  decideMockPublicTimesheet,
  getMockPublicTimesheet,
} from '@/__fixtures__/public-timesheet.mock';
import { fetchTimesheetCompletionMock } from '@/__fixtures__/timesheets.dynamic';
import { timesheets as mockTimesheets } from '@/__fixtures__/timesheets.mock';
import { canApproveInvoice } from '@/features/admin/billing/domain/approvalValidation';
import { computeFromLines } from '@/features/admin/billing/domain/calc';
import { Invoice, InvoiceSource, InvoiceStatus } from '@/features/admin/billing/types';
import { authStorage } from '@/shared/libs/authStorage';
import { AdminAnnouncement } from '@/shared/types/admin-announcements';
import { AdminBlogArticle, BlogArticleStatus } from '@/shared/types/admin-blog';
import { AdminUser } from '@/shared/types/admin-users';
import { ClientContact, ClientContactFormData } from '@/shared/types/client-contacts';
import { Client } from '@/shared/types/clients';
import { Consultant } from '@/shared/types/consultants';
import { AdminExpenseType } from '@/shared/types/expense-types';
import { ExpensesByMonth, ExpenseStatus } from '@/shared/types/expenses';
import {
  Currency,
  FeeType,
  Mission,
  MissionOrderForm,
  MissionStatus,
} from '@/shared/types/missions';
import { Timesheet } from '@/shared/types/timesheet';
import { AdminTimesheetActivity } from '@/shared/types/timesheet-activities';

// --- DB in-memory, mutable ---
const mockExpensesDB: ExpensesByMonth[] = JSON.parse(
  JSON.stringify(mockExpenses),
) as ExpensesByMonth[];

// Clients & contacts: DB in-memory (mutable, seed from fixture)
const mockClientsDB: Client[] = JSON.parse(JSON.stringify(mockClients)) as Client[];
const mockClientContactsDB: ClientContact[] = JSON.parse(
  JSON.stringify(mockClientContacts),
) as ClientContact[];

// Billing: invoices (mutable, seed from fixture)
const mockInvoicesDB: Invoice[] = JSON.parse(JSON.stringify(mockInvoices)) as Invoice[];

// Consultants: DB in-memory (mutable, seed from fixture)
const mockConsultantsDB: Consultant[] = JSON.parse(JSON.stringify(mockConsultants)) as Consultant[];

// Admin expense types: DB in-memory (mutable, seed)
const mockAdminAnnouncementsDB: AdminAnnouncement[] = [
  {
    id: 'ann-1',
    title: 'Bienvenue sur votre nouvel espace Suivora',
    description: 'Retrouvez ici vos CRA, notes de frais, documents…',
    content: '## Bienvenue 👋\n\nVotre espace **Suivora** centralise votre quotidien.',
    audience: 'ALL',
    status: 'PUBLISHED',
    tags: ['Général', 'Outils'],
    pinned: true,
    authorName: 'Admin Suivora',
    publishedAt: '2026-07-01T08:00:00.000Z',
    expiresAt: null,
    createdAt: '2026-07-01T08:00:00.000Z',
    updatedAt: '2026-07-01T08:00:00.000Z',
  },
  {
    id: 'ann-draft',
    title: 'Brouillon — à ne pas publier',
    description: 'Communiqué en cours de rédaction.',
    content: '## Brouillon',
    audience: 'ALL',
    status: 'DRAFT',
    tags: [],
    pinned: false,
    authorName: 'Admin Suivora',
    publishedAt: null,
    expiresAt: null,
    createdAt: '2026-07-10T08:00:00.000Z',
    updatedAt: '2026-07-10T08:00:00.000Z',
  },
];

const mockAdminBlogArticlesDB: AdminBlogArticle[] = [
  {
    id: 'blog-1',
    slug: 'bienvenue-sur-le-blog-suivora',
    category: 'Actualités',
    coverUrl: 'https://mock-cdn.suivora.test/blog/cover-1.jpg',
    readMinutes: 4,
    author: 'Admin Suivora',
    featured: true,
    status: 'published',
    scheduledAt: null,
    translations: [
      {
        locale: 'fr',
        title: 'Bienvenue sur le blog Suivora',
        excerpt: 'Actualités, conseils et coulisses du portage salarial.',
        highlights: ['Portage salarial', 'Conseils carrière'],
        contentMd: '## Bienvenue\n\nLe **blog Suivora** partage nos actualités et conseils.',
      },
      {
        locale: 'en',
        title: 'Welcome to the Suivora blog',
        excerpt: 'News, tips and behind-the-scenes of umbrella employment.',
        highlights: ['Umbrella employment', 'Career tips'],
        contentMd: '## Welcome\n\nThe **Suivora blog** shares our news and tips.',
      },
    ],
    publishedAt: '2026-07-01T08:00:00.000Z',
    createdAt: '2026-07-01T08:00:00.000Z',
    updatedAt: '2026-07-01T08:00:00.000Z',
    viewCount: 128,
  },
  {
    id: 'blog-2',
    slug: 'optimiser-sa-remuneration-en-portage',
    category: 'Fiscalité',
    coverUrl: 'https://mock-cdn.suivora.test/blog/cover-2.jpg',
    readMinutes: 6,
    author: 'Mélanie Spillers',
    featured: false,
    status: 'published',
    scheduledAt: null,
    translations: [
      {
        locale: 'fr',
        title: 'Optimiser sa rémunération en portage',
        excerpt: 'Les leviers pour maximiser votre net en portage salarial.',
        highlights: ['Frais professionnels', 'Dividendes'],
        contentMd: '## Optimiser\n\nQuelques leviers concrets pour votre rémunération.',
      },
      {
        locale: 'en',
        title: 'Optimising your umbrella pay',
        excerpt: 'Levers to maximise your net income under umbrella employment.',
        highlights: ['Business expenses', 'Dividends'],
        contentMd: '## Optimising\n\nA few concrete levers for your compensation.',
      },
    ],
    publishedAt: '2026-06-20T09:00:00.000Z',
    createdAt: '2026-06-20T09:00:00.000Z',
    updatedAt: '2026-06-20T09:00:00.000Z',
    viewCount: 342,
  },
  {
    id: 'blog-draft',
    slug: null,
    category: 'Guides',
    coverUrl: '',
    readMinutes: 5,
    author: 'Admin Suivora',
    featured: false,
    status: 'draft',
    scheduledAt: null,
    translations: [
      {
        locale: 'fr',
        title: 'Brouillon — guide à venir',
        excerpt: 'Article en cours de rédaction.',
        highlights: [],
        contentMd: '## Brouillon',
      },
    ],
    publishedAt: null,
    createdAt: '2026-07-10T08:00:00.000Z',
    updatedAt: '2026-07-10T08:00:00.000Z',
    viewCount: 0,
  },
];

function slugifyMock(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 80);
}

function blogKpis() {
  const by = (s: BlogArticleStatus) => mockAdminBlogArticlesDB.filter(a => a.status === s).length;
  return {
    total: mockAdminBlogArticlesDB.length,
    draft: by('draft'),
    scheduled: by('scheduled'),
    published: by('published'),
    archived: by('archived'),
    featured: mockAdminBlogArticlesDB.filter(a => a.featured).length,
  };
}

/** Communiqués lus par le compte mock (§4). Seed depuis les fixtures (read=true). */
const mockAnnouncementReads = new Set<string>(mockAnnouncements.filter(a => a.read).map(a => a.id));

const mockExpenseTypesDB: AdminExpenseType[] = [
  {
    id: 'xt_1',
    key: 'transport',
    value: 'transport',
    label: 'Transport',
    scope: 'GLOBAL',
    isMileage: false,
    requiresReceipt: true,
    sortOrder: 1,
    isActive: true,
  },
  {
    id: 'xt_2',
    key: 'repas',
    value: 'repas',
    label: 'Repas',
    scope: 'GLOBAL',
    isMileage: false,
    requiresReceipt: true,
    sortOrder: 2,
    isActive: true,
  },
  {
    id: 'xt_3',
    key: 'fournitures',
    value: 'fournitures',
    label: 'Fournitures',
    scope: 'INTERNAL',
    isMileage: false,
    requiresReceipt: true,
    sortOrder: 3,
    isActive: true,
  },
  {
    id: 'xt_4',
    key: 'ik',
    value: 'ik',
    label: 'Indemnités kilométriques',
    scope: 'GLOBAL',
    isMileage: true,
    requiresReceipt: false,
    sortOrder: 4,
    isActive: true,
  },
];

// Admin timesheet activities: DB in-memory (mutable, seed)
const mockTimesheetActivitiesDB: AdminTimesheetActivity[] = [
  {
    id: 'ta_1',
    key: 'internal_project',
    type: 'project',
    value: 'internal_project',
    label: 'Projet interne',
    group: 'Interne',
    scope: 'INTERNAL',
    allowRemoteFlag: true,
    requiresJustification: false,
    justificationHint: null,
    sortOrder: 1,
    isActive: true,
  },
  {
    id: 'ta_2',
    key: 'school',
    type: 'project',
    value: 'school',
    label: 'École / Formation',
    group: 'Interne',
    scope: 'INTERNAL',
    allowRemoteFlag: false,
    requiresJustification: false,
    justificationHint: null,
    sortOrder: 2,
    isActive: true,
  },
  {
    id: 'ta_3',
    key: 'paid_leave',
    type: 'justifiedAbsence',
    value: 'paid_leave',
    label: 'Congés payés',
    group: 'Absences',
    scope: 'GLOBAL',
    allowRemoteFlag: false,
    requiresJustification: false,
    justificationHint: null,
    sortOrder: 3,
    isActive: true,
  },
  {
    id: 'ta_4',
    key: 'sick_leave',
    type: 'absenceToBeJustified',
    value: 'sick_leave',
    label: 'Absence maladie',
    group: 'Absences',
    scope: 'GLOBAL',
    allowRemoteFlag: false,
    requiresJustification: true,
    justificationHint: 'Joindre un arrêt de travail',
    sortOrder: 4,
    isActive: true,
  },
];

// Admin users: DB in-memory (seed)
const mockAdminUsersDB: AdminUser[] = [
  {
    id: 'admin_user_1',
    firstName: 'Sarah',
    lastName: 'Bernard',
    email: 'sarah.bernard@suivora.test',
    role: 'admin',
    status: 'ACTIVE',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'admin_user_2',
    firstName: 'Kamel',
    lastName: 'Durand',
    email: 'kamel.durand@suivora.test',
    role: 'rh',
    status: 'ACTIVE',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'admin_user_3',
    firstName: 'Nadia',
    lastName: 'Leroy',
    email: 'nadia.leroy@suivora.test',
    role: 'manager',
    status: 'ACTIVE',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'admin_user_4',
    firstName: 'Julien',
    lastName: 'Martin',
    email: 'julien.martin@suivora.test',
    role: 'user',
    status: 'INACTIVE',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'admin_user_5',
    firstName: 'Ines',
    lastName: 'Petit',
    email: 'ines.petit@suivora.test',
    role: 'admin',
    status: 'ACTIVE',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'admin_user_6',
    firstName: 'Thomas',
    lastName: 'Roux',
    email: 'thomas.roux@suivora.test',
    role: 'manager',
    status: 'ACTIVE',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// Missions: DB in-memory (vide par défaut)
const mockMissionsDB: Mission[] = [];

function toStatus(v: string | null): ExpenseStatus | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (s === 'validated') return ExpenseStatus.VALIDATED;
  if (s === 'pending') return ExpenseStatus.PENDING;
  if (s === 'refused') return ExpenseStatus.REFUSED;
  if (s === 'created') return ExpenseStatus.CREATED;
  if (s === 'open') return ExpenseStatus.OPEN;
  return null;
}

function sortMonthEntries(arr: ExpensesByMonth[], sort: string | null) {
  // sort=asc|desc par (year, month) si demandé, sinon inchangé
  if (!sort) return arr;
  const dir = sort === 'desc' ? -1 : 1;
  return [...arr].sort((a, b) => {
    if (a.year !== b.year) return (a.year - b.year) * dir;
    return (a.month - b.month) * dir;
  });
}

function parseBody<T = any>(options?: RequestInit): T | undefined {
  if (!options?.body) return undefined;
  try {
    if (typeof options.body === 'string') {
      return JSON.parse(options.body) as T;
    }
    return options.body as unknown as T;
  } catch {
    return undefined;
  }
}

function ok<T>(data: T): T {
  return data;
}

function fail(message: string, status = 400): never {
  const err = new Error(message) as Error & { status?: number };
  err.status = status;
  throw err;
}

function readBearerFromHeaders(init?: RequestInit) {
  const h = new Headers((init?.headers as any) ?? {});
  const auth = h.get('Authorization') ?? h.get('authorization');
  if (!auth) return null;
  const m = auth.match(/^Bearer\s+(.+)$/i);
  return m?.[1] ?? null;
}

/**
 * Resolve a valid mock token from different possible sources:
 *  1. Authorization header (Bearer ...)
 *  2. localStorage via `authStorage`
 *  3. In-memory mock token from fixtures (fallback)
 */
function resolveMockToken(options?: RequestInit): string | null {
  const headerToken = readBearerFromHeaders(options);
  const localToken = authStorage.getAccessToken();
  const memToken = getMockAccessToken();
  return headerToken ?? localToken ?? memToken ?? null;
}

function getPathAndSearch(url: string) {
  // Create an absolute URL to parse search params during dev
  const base = 'http://localhost';
  const u = new URL(url, base);
  return { path: u.pathname, search: u.searchParams };
}

/** Utility to safely parse integers from query params */
function toInt(v: string | null, d: number): number {
  const n = Number(v ?? '');
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : d;
}

type ParsedInvoiceNumber = { year?: number; seq: number };

function parseInvoiceNumber(num: string): ParsedInvoiceNumber | null {
  const t = num.trim().toUpperCase();

  // Format accepté :
  // - FAC-AAAA-00001
  // - FAC-0002
  const mYearBased = t.match(/^FAC-(\d{4})-(\d{1,5})$/);
  if (mYearBased) {
    const year = parseInt(mYearBased[1]!, 10);
    const seq = parseInt(mYearBased[2]!, 10);
    return { year, seq };
  }

  const mYearless = t.match(/^FAC-(\d{1,5})$/);
  if (mYearless) {
    const seq = parseInt(mYearless[1]!, 10);
    return { seq };
  }

  return null;
}

function isValidInvoiceNumber(num: string): boolean {
  return parseInvoiceNumber(num) != null;
}

/** Prochaine facture basée uniquement sur les factures confirmées (pas les brouillons),
 * en excluant les factures créées manuellement.
 */
function getNextConfirmedInvoiceNumber(
  invoices: { id: string; number?: string; status?: InvoiceStatus; source?: InvoiceSource }[],
): string {
  const year = new Date().getFullYear();
  let maxSeq = 0;

  for (const inv of invoices) {
    if (inv.status === InvoiceStatus.DRAFT) continue;
    if (inv.source === InvoiceSource.BILLING_FORM) continue;
    if (!inv.number?.trim()) continue;

    const parsed = parseInvoiceNumber(inv.number);
    if (!parsed) continue;

    // Pour la version avec année, on ne compte que l'année courante.
    if (parsed.year != null && parsed.year !== year) continue;

    if (parsed.seq > maxSeq) maxSeq = parsed.seq;
  }

  let nextSeq = maxSeq + 1;
  // On ignore les factures BILLING_FORM (API: MANUAL) pour l'incrément, mais on garantit quand même
  // l'unicité par rapport à toutes les factures confirmées.
  // (sinon on risque d'obtenir un numéro déjà utilisé puis échanger au moment de l'approve)
  while (
    isInvoiceNumberUsedByConfirmedInvoices(
      invoices,
      `FAC-${year}-${String(nextSeq).padStart(5, '0')}`,
    )
  ) {
    nextSeq += 1;
  }

  return `FAC-${year}-${String(nextSeq).padStart(5, '0')}`;
}

/** Vérifie les conflits de numéros parmi les factures confirmées.
 * On compare par (seq) et, si les deux numéros ont une année, par année.
 */
function isInvoiceNumberUsedByConfirmedInvoices(
  invoices: { id: string; number?: string; status?: InvoiceStatus }[],
  number: string,
  excludeId?: string,
): boolean {
  const candidate = parseInvoiceNumber(number);
  if (!candidate) return false;

  return invoices.some(i => {
    if (i.id === excludeId) return false;
    if (i.status === InvoiceStatus.DRAFT) return false;
    if (!i.number?.trim()) return false;

    const existing = parseInvoiceNumber(i.number);
    if (!existing) return false;

    if (existing.seq !== candidate.seq) return false;
    if (candidate.year != null && existing.year != null && candidate.year !== existing.year)
      return false;

    return true;
  });
}

/** Utility to paginate mock data arrays */
function paginate<T>(all: T[], page: number, limit: number) {
  const total = all.length;
  const last = total === 0 ? 1 : Math.max(1, Math.ceil(total / limit));
  const current = Math.min(Math.max(1, page), last);
  const start = (current - 1) * limit;
  const end = start + limit;

  const data = all.slice(start, end);
  const previous = current > 1 ? current - 1 : null;
  const next = current < last ? current + 1 : null;

  return {
    pagination: { current, limit, total, next, previous, last },
    data,
  };
}

/**
 * A mock HTTP client that simulates backend API behavior.
 *
 * Features:
 * - Replaces `httpClient` / `publicHttpClient` when `NEXT_PUBLIC_API_IMPL === 'mock'`.
 * - Provides mocked endpoints for authentication, announcements, tasks, timesheets, expenses, and user details.
 * - Supports pagination and token validation simulation.
 * - Adds artificial latency for more realistic UX in dev/testing.
 *
 * @template T - Expected response type.
 * @param {string} endpoint - The endpoint being requested.
 * @param {RequestInit} [options={}] - Optional request init parameters.
 * @returns {Promise<T>} Mocked response of type `T`.
 * @throws Will throw an error for unauthorized access or non-mocked endpoints.
 */
export async function mockHttpClient<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  // Simulate slight network latency
  await new Promise(r => setTimeout(r, 120));

  const method = (options.method || 'GET').toUpperCase();
  const url = endpoint; // in mock mode we use the endpoint as-is
  const { path, search } = getPathAndSearch(url);

  /* -------------------------- AUTH (mock) -------------------------- */

  // POST /api/auth/login  (or /api/v1/auth/login)
  if (method === 'POST' && (url === '/api/auth/login' || url === '/api/v1/auth/login')) {
    const body = parseBody<{ email?: string; password?: string }>(options);
    if (!body?.email || !body?.password) {
      return fail('Veuillez entrer votre email et votre mot de passe', 400);
    }
    const isValid =
      body.email === mockCredentials.email && body.password === mockCredentials.password;
    if (!isValid) {
      return fail('Invalid credentials', 401);
    }

    // Save token in memory mock + localStorage (used by real httpClient)
    setMockAccessToken(mockTokens.accessToken);
    authStorage.setAccessToken(mockTokens.accessToken);

    const payload = {
      accessToken: mockTokens.accessToken,
      // refreshToken: mockTokens.refreshToken, // enable if you want to test rotation
      user: {
        ...mockUser,
        profileType: mockUser.profileType ?? 'CONSULTANT',
        capabilities: deriveMockCapabilities(mockUser),
      },
    };
    return ok(payload as unknown as T);
  }

  // POST /api/auth/refresh-token (or /api/v1/auth/refresh-token)
  // Always returns a new mock token
  if (
    method === 'POST' &&
    (url === '/api/auth/refresh-token' || url === '/api/v1/auth/refresh-token')
  ) {
    const newToken = `${mockTokens.accessToken}_refreshed`;
    setMockAccessToken(newToken);
    authStorage.setAccessToken(newToken);
    return ok({ accessToken: newToken } as unknown as T);
  }

  // POST /api/auth/logout
  if (method === 'POST' && url === '/api/auth/logout') {
    // Normally, backend would revoke token + clear cookie
    // Here: just clear mock state + localStorage
    setMockAccessToken(null);
    authStorage.setAccessToken('');
    return ok({ ok: true } as unknown as T);
  }

  // GET /api/v1/auth/me
  if (method === 'GET' && url === '/api/v1/auth/me') {
    const token = resolveMockToken(options);
    if (!token) {
      return fail('Unauthorized', 401);
    }
    const me = {
      ...mockUser,
      profileType: mockUser.profileType ?? 'CONSULTANT',
      capabilities: deriveMockCapabilities(mockUser),
    };
    return ok(me as unknown as T);
  }

  // GET /api/v1/me/internal-remuneration (carte « Ma rémunération »)
  if (method === 'GET' && url === '/api/v1/me/internal-remuneration') {
    const token = resolveMockToken(options);
    if (!token) {
      return fail('Unauthorized', 401);
    }
    return ok({
      monthlyNet: 1400,
      currency: 'EUR',
      cumulativeSince: '2025-09-01',
      cumulativeNet: 15400,
    } as unknown as T);
  }

  // GET /api/v1/me/revenue?year&month (CA du mois + cumul mission)
  if (method === 'GET' && path === '/api/v1/me/revenue') {
    const token = resolveMockToken(options);
    if (!token) return fail('Unauthorized', 401);
    return ok({
      currency: 'EUR',
      month: { workedDays: 18, grossRevenue: 9000 },
      mission: { workedDays: 210, grossRevenue: 105000 },
    } as unknown as T);
  }

  // GET /api/v1/me/compensation?months=N (historique brut/net/dividendes vs CA)
  if (method === 'GET' && path === '/api/v1/me/compensation') {
    const token = resolveMockToken(options);
    if (!token) return fail('Unauthorized', 401);
    const n = Math.min(Math.max(toInt(search.get('months'), 6), 1), 24);
    const now = new Date();
    const months = Array.from({ length: n }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (n - 1 - i), 1);
      return {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        grossRevenue: 9000,
        grossSalary: 4200,
        netSalary: 3100,
        dividends: i === n - 1 ? 1500 : 0,
      };
    });
    return ok({ currency: 'EUR', months } as unknown as T);
  }

  // GET /api/v1/me/announcements[?tag=] (filtrés par audience + tag, triés)
  if (method === 'GET' && path === '/api/v1/me/announcements') {
    const token = resolveMockToken(options);
    if (!token) {
      return fail('Unauthorized', 401);
    }
    const profile = mockUser.profileType ?? 'CONSULTANT';
    const tag = search.get('tag');
    const visible = mockAnnouncements
      .filter(a => a.audience === 'ALL' || a.audience === profile)
      .filter(a => !tag || (a.tags ?? []).includes(tag))
      .sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
      })
      .map(a => ({ ...a, read: mockAnnouncementReads.has(a.id) }));
    return ok(visible as unknown as T);
  }

  // GET /api/v1/me/announcements/unread-count → { count }
  if (method === 'GET' && path === '/api/v1/me/announcements/unread-count') {
    const token = resolveMockToken(options);
    if (!token) return fail('Unauthorized', 401);
    const profile = mockUser.profileType ?? 'CONSULTANT';
    const count = mockAnnouncements
      .filter(a => a.audience === 'ALL' || a.audience === profile)
      .filter(a => !mockAnnouncementReads.has(a.id)).length;
    return ok({ count } as unknown as T);
  }

  // POST /api/v1/me/announcements/read-all → tout marquer lu (204 idempotent)
  if (method === 'POST' && path === '/api/v1/me/announcements/read-all') {
    const token = resolveMockToken(options);
    if (!token) return fail('Unauthorized', 401);
    const profile = mockUser.profileType ?? 'CONSULTANT';
    mockAnnouncements
      .filter(a => a.audience === 'ALL' || a.audience === profile)
      .forEach(a => mockAnnouncementReads.add(a.id));
    return ok(null as unknown as T);
  }

  // POST /api/v1/me/announcements/:id/read → marque lu (204 idempotent)
  if (method === 'POST' && path.startsWith('/api/v1/me/announcements/') && path.endsWith('/read')) {
    const token = resolveMockToken(options);
    if (!token) return fail('Unauthorized', 401);
    const id = path.slice('/api/v1/me/announcements/'.length, -'/read'.length);
    mockAnnouncementReads.add(id);
    return ok(null as unknown as T);
  }

  // POST /api/v1/auth/password/reset/request
  if (
    method === 'POST' &&
    (url === '/api/v1/auth/password/reset/request' ||
      url === '/api/auth/forgot-password' ||
      url === '/api/v1/auth/forgot-password')
  ) {
    const body = parseBody<{ email?: string }>(options);
    if (!body?.email) {
      return fail('Email is required', 400);
    }

    return ok({
      message: 'If an account exists, a reset link has been sent.',
    } as unknown as T);
  }

  // POST /api/auth/set-password | /api/auth/reset-password
  if (
    method === 'POST' &&
    (url === '/api/auth/set-password' || url === '/api/auth/reset-password')
  ) {
    const body = parseBody<{ token?: string; password?: string }>(options);
    if (!body?.token || !body?.password) {
      return fail('Token et mot de passe requis', 400);
    }
    if (body.password.length < 8) {
      return fail('Le mot de passe doit contenir au moins 8 caractères', 400);
    }

    return ok({
      success: true,
      message: 'Mot de passe défini avec succès',
      accessToken: 'mock.access.token.' + Math.random().toString(36).slice(2),
      user: {
        id: url === '/api/auth/reset-password' ? 'user_123' : 'admin_user_mock',
        email: url === '/api/auth/reset-password' ? mockCredentials.email : 'invite@suivora.test',
        firstName: url === '/api/auth/reset-password' ? 'John' : 'Invité',
        lastName: url === '/api/auth/reset-password' ? 'Doe' : 'Mock',
        roles: url === '/api/auth/reset-password' ? ['user'] : ['admin'],
      },
    } as unknown as T);
  }

  /* -------------------- PUBLIC TIMESHEET / CRA (mock) -------------------- */
  // GET /api/v1/public/timesheet/:token
  if (method === 'GET' && path.startsWith('/api/v1/public/timesheet/')) {
    const token = path.split('/').pop()!;
    if (token === 'invalid') return fail('Lien invalide', 404);
    if (token === 'expired') return fail('Ce lien a expiré', 400);
    return ok(getMockPublicTimesheet(token) as unknown as T);
  }

  // POST /api/v1/public/timesheet/:token
  if (method === 'POST' && path.startsWith('/api/v1/public/timesheet/')) {
    const token = path.split('/').pop()!;
    if (token === 'invalid') return fail('Lien invalide', 404);
    if (token === 'expired') return fail('Ce lien a expiré', 400);

    const current = getMockPublicTimesheet(token);
    if (current.decided) return fail('Ce lien a déjà été traité', 400);

    const body = parseBody<{ approved?: boolean; comment?: string }>(options);
    const approved = Boolean(body?.approved);
    if (!approved && !body?.comment?.trim()) {
      return fail('Le motif du renvoi est obligatoire', 400);
    }
    return ok(decideMockPublicTimesheet(token, approved) as unknown as T);
  }

  /* -------------------- SECURE ZONE (mock) -------------------- */
  const secureRoutes = [
    '/api/v1/users/',
    '/api/v1/files',
    '/api/v1/expenses',
    '/api/v1/announcements',
    '/api/v1/missions',
    '/api/v1/timesheets',
    '/api/v1/timesheets/activity-options',
    '/api/v1/documents',
    '/api/v1/admin/clients',
    '/api/v1/admin/client-contacts',
    '/api/v1/admin/invoices',
    '/api/v1/admin/consultants',
    '/api/v1/admin/users',
    '/api/v1/admin/expense-types',
    '/api/v1/admin/announcements',
    '/api/v1/admin/blog-articles',
    '/api/v1/admin/timesheet-activities',
    '/api/v1/admin/missions',
    '/api/v1/admin/hr-inbox',
  ];
  if (secureRoutes.some(p => path === p || path.startsWith(p))) {
    const token = resolveMockToken(options);
    if (!token) return fail('Unauthorized', 401);
  }

  /* -------------------- FILES (mock upload NF) -------------------- */
  if (method === 'POST' && path === '/api/v1/files/expense-receipts') {
    const token = resolveMockToken(options);
    if (!token) return fail('Unauthorized', 401);
    return ok({
      url: 'https://mock-cdn.suivora.test/receipts/mock-justificatif.pdf',
    } as unknown as T);
  }

  /* -------------------- ANNOUNCEMENTS (paginated) -------------------- */
  if (method === 'GET' && path === '/api/v1/announcements') {
    const page = toInt(search.get('page'), 1);
    const limit = toInt(search.get('limit'), 10);

    const sorted = [...mockAnnouncements].sort(
      (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
    );

    const payload = paginate(sorted, page, limit);
    return ok(payload as unknown as T);
  }

  /* -------------------- ACTIVITY OPTIONS (non paginé) -------------------- */
  if (method === 'GET' && path === '/api/v1/timesheets/activity-options') {
    const token = resolveMockToken(options);
    let userId = mockUser?.id ?? 'guest';

    if (token?.includes('user_123')) userId = 'user_123';

    const optionsForUser = getActivityOptionsForUser(userId);
    return ok(optionsForUser as unknown as T);
  }

  /* -------------------- MISSIONS du consultant connecté -------------------- */
  if (method === 'GET' && path === '/api/v1/missions') {
    const token = resolveMockToken(options);
    let uid = mockUser?.id ?? 'guest';
    if (token?.includes('user_123')) uid = 'user_123';
    return ok(getMissionsForUser(uid) as unknown as T);
  }

  /* -------------------- TIMESHEET par mois (GET) -------------------- */
  if (method === 'GET') {
    const payfitReconciliationGet =
      /^\/api\/v1\/timesheets\/(\d{4})\/(\d{1,2})\/payroll-reconciliation$/.exec(path);
    if (payfitReconciliationGet) {
      return ok({
        available: false,
        payfitLinked: false,
        absences: [],
        payfitAbsenceDays: 0,
        timesheetAbsenceDays: null,
        hasDiscrepancy: false,
      } as unknown as T);
    }

    const detailGet = /^\/api\/v1\/timesheets\/(\d{4})\/(\d{1,2})$/.exec(path);
    if (detailGet) {
      const year = Number(detailGet[1]);
      const month = Number(detailGet[2]);
      const row = mockTimesheets.find(t => t.year === year && t.month === month);
      if (!row) {
        return fail('CRA introuvable', 404);
      }
      return ok(row as unknown as T);
    }
  }

  /* -------------------- TIMESHEETS (paginated) -------------------- */
  if (method === 'GET' && path === '/api/v1/timesheets') {
    const page = toInt(search.get('page'), 1);
    const limit = toInt(search.get('limit'), 20);

    const payload = paginate([...mockTimesheets], page, limit);
    return ok(payload as unknown as T);
  }

  /* -------------------- TIMESHEETS completion-summary (dashboard) -------------------- */
  if (method === 'GET' && path === '/api/v1/timesheets/completion-summary') {
    const token = resolveMockToken(options);
    let uid = mockUser?.id ?? 'guest';
    if (token?.includes('user_123')) uid = 'user_123';
    const summary = await fetchTimesheetCompletionMock(uid);
    return ok(summary as unknown as T);
  }

  /* -------------------- TIMESHEETS PATCH (saver) -------------------- */
  if (method === 'PATCH') {
    const m = /^\/api\/v1\/timesheets\/(\d{4})\/(\d{1,2})$/.exec(path);
    if (m) {
      const year = Number(m[1]);
      const month = Number(m[2]);
      const body = parseBody<{
        timesheetStatus: string;
        workingDays: number;
        totalRemoteWorkDays: number;
        totalWorkedDays: number;
        totalAbsenceDays: number;
        hasOnCallDuty: boolean;
        comment?: string;
        activities: unknown[];
      }>(options);
      if (
        !body ||
        typeof body.workingDays !== 'number' ||
        !Array.isArray(body.activities) ||
        typeof body.timesheetStatus !== 'string'
      ) {
        return fail('Payload CRA invalide', 400);
      }
      const idx = mockTimesheets.findIndex(t => t.year === year && t.month === month);
      const merged = {
        id: idx >= 0 && mockTimesheets[idx]?.id ? mockTimesheets[idx].id! : crypto.randomUUID(),
        userId: mockUser?.id ?? '23456',
        year,
        month,
        timesheetStatus: body.timesheetStatus,
        workingDays: body.workingDays,
        totalRemoteWorkDays: body.totalRemoteWorkDays,
        totalWorkedDays: body.totalWorkedDays,
        totalAbsenceDays: body.totalAbsenceDays,
        hasOnCallDuty: body.hasOnCallDuty,
        comment: body.comment ?? '',
        activities: body.activities,
      };
      if (idx >= 0) {
        mockTimesheets[idx] = merged as Timesheet;
      } else {
        mockTimesheets.push(merged as Timesheet);
      }
      return ok(merged as unknown as T);
    }
  }

  /* -------------------------- ADMIN ANNOUNCEMENTS (CRUD) -------------------------- */
  if (path === '/api/v1/admin/announcements') {
    if (method === 'GET') {
      return ok([...mockAdminAnnouncementsDB] as unknown as T);
    }
    if (method === 'POST') {
      const body = parseBody<Record<string, unknown>>(options) ?? {};
      const nowIso = mockAdminAnnouncementsDB[0]?.createdAt ?? '2026-07-01T00:00:00.000Z';
      const created = {
        id: `ann_${mockAdminAnnouncementsDB.length + 1}`,
        authorName: 'Admin Suivora',
        tags: [],
        pinned: false,
        publishedAt: body.status === 'PUBLISHED' ? nowIso : null,
        expiresAt: null,
        createdAt: nowIso,
        updatedAt: nowIso,
        ...body,
      };
      mockAdminAnnouncementsDB.unshift(created as unknown as AdminAnnouncement);
      return ok(created as unknown as T);
    }
  }
  if (method === 'GET' && path.startsWith('/api/v1/admin/announcements/')) {
    const id = path.split('/').pop()!;
    const found = mockAdminAnnouncementsDB.find(x => x.id === id);
    if (!found) return fail('Announcement not found', 404);
    return ok(found as unknown as T);
  }
  if (method === 'PATCH' && path.startsWith('/api/v1/admin/announcements/')) {
    const id = path.split('/').pop()!;
    const idx = mockAdminAnnouncementsDB.findIndex(x => x.id === id);
    if (idx === -1) return fail('Announcement not found', 404);
    const body = parseBody<Record<string, unknown>>(options) ?? {};
    const prev = mockAdminAnnouncementsDB[idx];
    const publishedAt =
      body.status === 'PUBLISHED' && !prev.publishedAt ? prev.createdAt : prev.publishedAt;
    mockAdminAnnouncementsDB[idx] = { ...prev, ...body, publishedAt };
    return ok(mockAdminAnnouncementsDB[idx] as unknown as T);
  }
  if (method === 'DELETE' && path.startsWith('/api/v1/admin/announcements/')) {
    const id = path.split('/').pop()!;
    const idx = mockAdminAnnouncementsDB.findIndex(x => x.id === id);
    if (idx === -1) return fail('Announcement not found', 404);
    mockAdminAnnouncementsDB.splice(idx, 1);
    return ok({ success: true } as unknown as T);
  }

  /* -------------------------- ADMIN BLOG ARTICLES (CRUD) -------------------------- */
  if (path === '/api/v1/admin/blog-articles') {
    if (method === 'GET') {
      return ok({
        data: [...mockAdminBlogArticlesDB],
        kpis: blogKpis(),
        meta: { total: mockAdminBlogArticlesDB.length, page: 1, pageSize: 20 },
      } as unknown as T);
    }
    if (method === 'POST') {
      const body = parseBody<Record<string, unknown>>(options) ?? {};
      const nowIso = mockAdminBlogArticlesDB[0]?.createdAt ?? '2026-07-01T00:00:00.000Z';
      const translations = (body.translations as AdminBlogArticle['translations']) ?? [];
      const firstTitle = translations[0]?.title ?? 'article';
      const created: AdminBlogArticle = {
        id: `blog_${mockAdminBlogArticlesDB.length + 1}`,
        slug: slugifyMock(firstTitle),
        category: '',
        coverUrl: '',
        readMinutes: 3,
        author: 'Admin Suivora',
        featured: false,
        status: 'draft',
        scheduledAt: null,
        translations,
        publishedAt: body.status === 'published' ? nowIso : null,
        createdAt: nowIso,
        updatedAt: nowIso,
        viewCount: 0,
        ...(body as Partial<AdminBlogArticle>),
      };
      mockAdminBlogArticlesDB.unshift(created);
      return ok(created as unknown as T);
    }
  }
  if (method === 'GET' && path.startsWith('/api/v1/admin/blog-articles/')) {
    const id = path.split('/').pop()!;
    const found = mockAdminBlogArticlesDB.find(x => x.id === id);
    if (!found) return fail('Article not found', 404);
    return ok(found as unknown as T);
  }
  if (method === 'PATCH' && path.startsWith('/api/v1/admin/blog-articles/')) {
    const id = path.split('/').pop()!;
    const idx = mockAdminBlogArticlesDB.findIndex(x => x.id === id);
    if (idx === -1) return fail('Article not found', 404);
    const body = parseBody<Partial<AdminBlogArticle>>(options) ?? {};
    const prev = mockAdminBlogArticlesDB[idx];
    const publishedAt =
      body.status === 'published' && !prev.publishedAt ? prev.createdAt : prev.publishedAt;
    mockAdminBlogArticlesDB[idx] = {
      ...prev,
      ...body,
      publishedAt,
      updatedAt: prev.updatedAt,
    };
    return ok(mockAdminBlogArticlesDB[idx] as unknown as T);
  }
  if (method === 'DELETE' && path.startsWith('/api/v1/admin/blog-articles/')) {
    const id = path.split('/').pop()!;
    const idx = mockAdminBlogArticlesDB.findIndex(x => x.id === id);
    if (idx === -1) return fail('Article not found', 404);
    mockAdminBlogArticlesDB.splice(idx, 1);
    return ok({ success: true } as unknown as T);
  }

  /* -------------------------- ADMIN DASHBOARD STATS -------------------------- */
  if (method === 'GET' && path === '/api/v1/admin/dashboard/stats') {
    const token = resolveMockToken(options);
    if (!token) return fail('Unauthorized', 401);
    const now = new Date();
    const mths = (n: number) =>
      Array.from({ length: n }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (n - 1 - i), 1);
        return { year: d.getFullYear(), month: d.getMonth() + 1, i };
      });
    const billing = mths(12).map(({ year, month, i }) => {
      const invoiced = 58000 + ((i * 6197) % 42000);
      return { year, month, invoiced, collected: Math.round(invoiced * 0.88) };
    });
    return ok({
      currency: 'EUR',
      generatedAt: now.toISOString(),
      counts: {
        consultantsActive: 46,
        clients: 18,
        missionsActive: 31,
        timesheetsPending: 5,
        expensesPending: 3,
        signPending: 2,
        internalCount: 6,
      },
      financials: {
        invoicedThisMonth: billing[billing.length - 1].invoiced,
        invoicedThisMonthDeltaPct: 6.4,
        collectedThisMonth: billing[billing.length - 1].collected,
        outstanding: { count: 4, amount: 32000 },
        monthly: billing,
      },
      missionsByStatus: [
        { status: 'ACTIVE', count: 31 },
        { status: 'ENDED', count: 12 },
        { status: 'DRAFT', count: 4 },
      ],
      timesheetMonthly: mths(6).map(({ year, month, i }) => {
        const submitted = 24 + ((i * 7) % 12);
        const validated = submitted - (i % 3);
        return { year, month, submitted, validated, pending: submitted - validated };
      }),
      expenseMonthly: mths(6).map(({ year, month, i }) => ({
        year,
        month,
        amount: 3200 + ((i * 811) % 2600),
        count: 8 + ((i * 3) % 9),
      })),
      consultantsMonthly: mths(12).map(({ year, month, i }) => ({
        year,
        month,
        active: 34 + i,
        joined: 1 + (i % 4),
      })),
    } as unknown as T);
  }

  /* -------------------------- ADMIN EXPENSE TYPES (CRUD) -------------------------- */
  if (path === '/api/v1/admin/expense-types') {
    if (method === 'GET') {
      return ok([...mockExpenseTypesDB].sort((a, b) => a.sortOrder - b.sortOrder) as unknown as T);
    }
    if (method === 'POST') {
      const body = parseBody<Record<string, unknown>>(options) ?? {};
      const created = { id: `xt_${mockExpenseTypesDB.length + 1}`, ...body };
      mockExpenseTypesDB.unshift(created as (typeof mockExpenseTypesDB)[number]);
      return ok(created as unknown as T);
    }
  }
  if (method === 'PATCH' && path.startsWith('/api/v1/admin/expense-types/')) {
    const id = path.split('/').pop()!;
    const idx = mockExpenseTypesDB.findIndex(x => x.id === id);
    if (idx === -1) return fail('Expense type not found', 404);
    const body = parseBody<Record<string, unknown>>(options) ?? {};
    mockExpenseTypesDB[idx] = { ...mockExpenseTypesDB[idx], ...body };
    return ok(mockExpenseTypesDB[idx] as unknown as T);
  }
  if (method === 'DELETE' && path.startsWith('/api/v1/admin/expense-types/')) {
    const id = path.split('/').pop()!;
    const idx = mockExpenseTypesDB.findIndex(x => x.id === id);
    if (idx === -1) return fail('Expense type not found', 404);
    mockExpenseTypesDB.splice(idx, 1);
    return ok({ success: true } as unknown as T);
  }

  /* -------------------------- ADMIN TIMESHEET ACTIVITIES (CRUD) -------------------------- */
  if (path === '/api/v1/admin/timesheet-activities') {
    if (method === 'GET') {
      return ok(
        [...mockTimesheetActivitiesDB].sort((a, b) => a.sortOrder - b.sortOrder) as unknown as T,
      );
    }
    if (method === 'POST') {
      const body = parseBody<Record<string, unknown>>(options) ?? {};
      const created = { id: `ta_${mockTimesheetActivitiesDB.length + 1}`, ...body };
      mockTimesheetActivitiesDB.unshift(created as (typeof mockTimesheetActivitiesDB)[number]);
      return ok(created as unknown as T);
    }
  }
  if (method === 'PATCH' && path.startsWith('/api/v1/admin/timesheet-activities/')) {
    const id = path.split('/').pop()!;
    const idx = mockTimesheetActivitiesDB.findIndex(x => x.id === id);
    if (idx === -1) return fail('Activity not found', 404);
    const body = parseBody<Record<string, unknown>>(options) ?? {};
    mockTimesheetActivitiesDB[idx] = { ...mockTimesheetActivitiesDB[idx], ...body };
    return ok(mockTimesheetActivitiesDB[idx] as unknown as T);
  }
  if (method === 'DELETE' && path.startsWith('/api/v1/admin/timesheet-activities/')) {
    const id = path.split('/').pop()!;
    const idx = mockTimesheetActivitiesDB.findIndex(x => x.id === id);
    if (idx === -1) return fail('Activity not found', 404);
    mockTimesheetActivitiesDB.splice(idx, 1);
    return ok({ success: true } as unknown as T);
  }

  /* -------------------------- ADMIN USERS (CRUD + paginé) -------------------------- */
  if (path === '/api/v1/admin/users') {
    // GET /api/v1/admin/users[?page=&limit=]
    if (method === 'GET') {
      const sorted = [...mockAdminUsersDB].sort((a, b) =>
        `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`),
      );

      const page = toInt(search.get('page'), 1);
      const limit = toInt(search.get('limit'), 20);
      const hasPaginationParams = search.has('page') || search.has('limit');

      if (!hasPaginationParams) {
        return ok(sorted as unknown as T);
      }

      const payload = paginate(sorted, page, limit);
      return ok(payload as unknown as T);
    }

    // POST /api/v1/admin/users
    if (method === 'POST') {
      const body = parseBody<Partial<AdminUser>>(options);
      if (!body?.firstName || !body?.lastName || !body?.email) {
        return fail('Le prénom, nom et email sont requis', 400);
      }

      const now = new Date().toISOString();
      const created: AdminUser = {
        id: `admin_user_${Date.now()}`,
        firstName: String(body.firstName).trim(),
        lastName: String(body.lastName).trim(),
        email: String(body.email).trim(),
        role: (body.role as string) || 'user',
        status: (body.status as 'ACTIVE' | 'INACTIVE') ?? 'ACTIVE',
        createdAt: now,
        updatedAt: now,
      };

      mockAdminUsersDB.unshift(created);
      return ok(created as unknown as T);
    }
  }

  // PATCH /api/v1/admin/users/:id
  if (method === 'PATCH' && path.startsWith('/api/v1/admin/users/')) {
    const id = path.slice('/api/v1/admin/users/'.length);
    const body = parseBody<Partial<AdminUser>>(options);
    const idx = mockAdminUsersDB.findIndex(u => u.id === id);
    if (idx === -1) return fail('Utilisateur introuvable', 404);
    const existing = mockAdminUsersDB[idx];
    const updated: AdminUser = {
      ...existing,
      ...(body?.firstName != null && { firstName: String(body.firstName).trim() }),
      ...(body?.lastName != null && { lastName: String(body.lastName).trim() }),
      ...(body?.email != null && { email: String(body.email).trim() }),
      ...(body?.role !== undefined && { role: body.role }),
      ...(body?.status !== undefined && { status: body.status }),
      updatedAt: new Date().toISOString(),
    };
    mockAdminUsersDB[idx] = updated;
    return ok(updated as unknown as T);
  }

  // DELETE /api/v1/admin/users/:id
  if (method === 'DELETE' && path.startsWith('/api/v1/admin/users/')) {
    const id = path.slice('/api/v1/admin/users/'.length);
    const idx = mockAdminUsersDB.findIndex(u => u.id === id);
    if (idx === -1) return fail('Utilisateur introuvable', 404);
    mockAdminUsersDB.splice(idx, 1);
    return ok({ success: true } as unknown as T);
  }

  /* -------------------------- CONSULTANTS (CRUD + paginé) -------------------------- */
  if (path === '/api/v1/admin/consultants') {
    // GET /api/v1/admin/consultants[?page=&limit=]
    if (method === 'GET') {
      const sorted = [...mockConsultantsDB].sort((a, b) =>
        `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`),
      );

      const page = toInt(search.get('page'), 1);
      const limit = toInt(search.get('limit'), 20);
      const hasPaginationParams = search.has('page') || search.has('limit');

      if (!hasPaginationParams) {
        // Mode legacy: retourne la liste complète (sans pagination)
        return ok(sorted as unknown as T);
      }

      const payload = paginate(sorted, page, limit);
      return ok(payload as unknown as T);
    }

    // POST /api/v1/admin/consultants
    if (method === 'POST') {
      const body = parseBody<Partial<Consultant> & { inviteToPortal?: boolean }>(options);
      if (!body?.firstName || !body?.lastName || !body?.email) {
        return fail('Le prénom, nom et email sont requis', 400);
      }

      const now = new Date().toISOString();
      const created: Consultant = {
        id: `consultant_${Date.now()}`,
        firstName: String(body.firstName).trim(),
        lastName: String(body.lastName).trim(),
        email: String(body.email).trim(),
        phone: body.phone ? String(body.phone) : undefined,
        position: body.position ? String(body.position) : undefined,
        status: (body.status as 'ACTIVE' | 'INACTIVE') ?? 'ACTIVE',
        createdAt: now,
        updatedAt: now,
      };

      mockConsultantsDB.unshift(created);
      return ok({
        consultant: created,
        portalAccountLinked: body?.inviteToPortal !== false,
        invitationSent: body?.inviteToPortal !== false,
      } as unknown as T);
    }
  }

  // PATCH /api/v1/admin/consultants/:id
  if (method === 'PATCH' && path.startsWith('/api/v1/admin/consultants/')) {
    const id = path.slice('/api/v1/admin/consultants/'.length);
    const body = parseBody<Partial<Consultant>>(options);
    const idx = mockConsultantsDB.findIndex(c => c.id === id);
    if (idx === -1) return fail('Consultant introuvable', 404);
    const existing = mockConsultantsDB[idx];
    const updated: Consultant = {
      ...existing,
      ...(body?.firstName != null && { firstName: String(body.firstName).trim() }),
      ...(body?.lastName != null && { lastName: String(body.lastName).trim() }),
      ...(body?.email != null && { email: String(body.email).trim() }),
      ...(body?.phone !== undefined && {
        phone: body.phone ? String(body.phone).trim() : undefined,
      }),
      ...(body?.position !== undefined && {
        position: body.position ? String(body.position).trim() : undefined,
      }),
      ...(body?.status !== undefined && { status: body.status }),
      ...(body?.civility !== undefined && { civility: body.civility }),
      ...(body?.language !== undefined && { language: body.language }),
      ...(body?.isPorted !== undefined && { isPorted: Boolean(body.isPorted) }),
      updatedAt: new Date().toISOString(),
    };
    mockConsultantsDB[idx] = updated;
    return ok(updated as unknown as T);
  }

  // DELETE /api/v1/admin/consultants/:id
  if (method === 'DELETE' && path.startsWith('/api/v1/admin/consultants/')) {
    const id = path.slice('/api/v1/admin/consultants/'.length);
    const idx = mockConsultantsDB.findIndex(c => c.id === id);
    if (idx === -1) return fail('Consultant introuvable', 404);
    mockConsultantsDB.splice(idx, 1);
    return ok({ success: true } as unknown as T);
  }

  /* -------------------------- CLIENTS (CRUD + paginé) -------------------------- */
  if (path === '/api/v1/admin/clients') {
    // GET /api/v1/admin/clients[?page=&limit=]
    if (method === 'GET') {
      // tri alpha simple
      const sorted = [...mockClientsDB].sort((a, b) => a.name.localeCompare(b.name));

      const page = toInt(search.get('page'), 1);
      const limit = toInt(search.get('limit'), 20);
      const hasPaginationParams = search.has('page') || search.has('limit');

      if (!hasPaginationParams) {
        // Mode legacy: retourne la liste complète (sans pagination)
        return ok(sorted as unknown as T);
      }

      const payload = paginate(sorted, page, limit);
      return ok(payload as unknown as T);
    }

    // POST /api/v1/admin/clients
    if (method === 'POST') {
      const body = parseBody<Partial<Client>>(options);
      if (!body?.name || String(body.name).trim().length === 0) {
        return fail('Le nom du client est requis', 400);
      }

      const now = new Date().toISOString();
      const created: Client = {
        id: `client_${Date.now()}`,
        name: String(body.name).trim(),
        legalName: body.legalName ? String(body.legalName) : undefined,
        siret: body.siret ? String(body.siret) : undefined,
        vatNumber: body.vatNumber ? String(body.vatNumber) : undefined,
        email: body.email ? String(body.email) : undefined,
        phone: body.phone ? String(body.phone) : undefined,
        address: body.address ? String(body.address) : undefined,
        createdAt: now,
        updatedAt: now,
      };

      mockClientsDB.unshift(created);
      return ok(created as unknown as T);
    }
  }

  // PATCH /api/v1/admin/clients/:id
  if (method === 'PATCH' && path.startsWith('/api/v1/admin/clients/')) {
    const id = path.slice('/api/v1/admin/clients/'.length);
    const body = parseBody<Partial<Client>>(options);
    const idx = mockClientsDB.findIndex(c => c.id === id);
    if (idx === -1) return fail('Client introuvable', 404);
    const existing = mockClientsDB[idx];
    const updated: Client = {
      ...existing,
      ...(body?.name != null && { name: String(body.name).trim() }),
      ...(body?.legalName !== undefined && {
        legalName: body.legalName ? String(body.legalName) : undefined,
      }),
      ...(body?.siret !== undefined && { siret: body.siret ? String(body.siret) : undefined }),
      ...(body?.vatNumber !== undefined && {
        vatNumber: body.vatNumber ? String(body.vatNumber) : undefined,
      }),
      ...(body?.email !== undefined && { email: body.email ? String(body.email) : undefined }),
      ...(body?.phone !== undefined && { phone: body.phone ? String(body.phone) : undefined }),
      ...(body?.address !== undefined && {
        address: body.address ? String(body.address) : undefined,
      }),
      updatedAt: new Date().toISOString(),
    };
    mockClientsDB[idx] = updated;
    return ok(updated as unknown as T);
  }

  // DELETE /api/v1/admin/clients/:id
  if (method === 'DELETE' && path.startsWith('/api/v1/admin/clients/')) {
    const id = path.slice('/api/v1/admin/clients/'.length);
    const idx = mockClientsDB.findIndex(c => c.id === id);
    if (idx === -1) return fail('Client introuvable', 404);
    mockClientsDB.splice(idx, 1);
    return ok({ success: true } as unknown as T);
  }

  /* -------------------------- CLIENT CONTACTS (CRUD) -------------------------- */
  if (path === '/api/v1/admin/client-contacts') {
    // GET /api/v1/admin/client-contacts[?clientId=]
    if (method === 'GET') {
      let list = [...mockClientContactsDB];
      const filterClientId = search.get('clientId')?.trim();
      if (filterClientId) {
        list = list.filter(c => c.clientId === filterClientId);
      }
      list = list
        .map(c => ({
          ...c,
          clientName: c.clientName ?? mockClientsDB.find(cl => cl.id === c.clientId)?.name,
        }))
        .sort((a, b) =>
          `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`),
        );
      return ok(list as unknown as T);
    }

    // POST /api/v1/admin/client-contacts
    if (method === 'POST') {
      const body = parseBody<{
        clientId?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
        role?: string;
      }>(options);
      if (
        !body?.clientId ||
        !body?.firstName?.trim() ||
        !body?.lastName?.trim() ||
        !body?.email?.trim()
      ) {
        return fail('clientId, firstName, lastName et email sont requis', 400);
      }

      const client = mockClientsDB.find(c => c.id === body.clientId);
      const now = new Date().toISOString();
      const created: ClientContact = {
        id: `contact_${Date.now()}`,
        clientId: String(body.clientId).trim(),
        clientName: client?.name,
        firstName: String(body.firstName).trim(),
        lastName: String(body.lastName).trim(),
        email: String(body.email).trim(),
        phone: body.phone ? String(body.phone).trim() : undefined,
        role: body.role ? String(body.role).trim() : undefined,
        createdAt: now,
        updatedAt: now,
      };

      mockClientContactsDB.unshift(created);
      return ok(created as unknown as T);
    }
  }

  // PATCH /api/v1/admin/client-contacts/:id
  if (method === 'PATCH' && path.startsWith('/api/v1/admin/client-contacts/')) {
    const id = path.slice('/api/v1/admin/client-contacts/'.length);
    const body = parseBody<Partial<ClientContactFormData>>(options);
    const idx = mockClientContactsDB.findIndex(c => c.id === id);
    if (idx === -1) return fail('Contact introuvable', 404);

    const existing = mockClientContactsDB[idx];
    const updated: ClientContact = {
      ...existing,
      ...(body?.firstName != null && { firstName: String(body.firstName).trim() }),
      ...(body?.lastName != null && { lastName: String(body.lastName).trim() }),
      ...(body?.email != null && { email: String(body.email).trim() }),
      ...(body?.phone !== undefined && {
        phone: body.phone ? String(body.phone).trim() : undefined,
      }),
      ...(body?.role !== undefined && { role: body.role ? String(body.role).trim() : undefined }),
      ...(body?.clientId != null && {
        clientId: String(body.clientId).trim(),
        clientName: mockClientsDB.find(c => c.id === body!.clientId)?.name,
      }),
      updatedAt: new Date().toISOString(),
    };
    mockClientContactsDB[idx] = updated;
    return ok(updated as unknown as T);
  }

  // DELETE /api/v1/admin/client-contacts/:id
  if (method === 'DELETE' && path.startsWith('/api/v1/admin/client-contacts/')) {
    const id = path.slice('/api/v1/admin/client-contacts/'.length);
    const idx = mockClientContactsDB.findIndex(c => c.id === id);
    if (idx === -1) return fail('Contact introuvable', 404);
    mockClientContactsDB.splice(idx, 1);
    return ok({ success: true } as unknown as T);
  }

  /* -------------------------- BILLING (invoices) -------------------------- */
  if (path === '/api/v1/admin/invoices') {
    if (method === 'GET') {
      const sorted = [...mockInvoicesDB].sort((a, b) =>
        (b.issuedAt ?? '').localeCompare(a.issuedAt ?? ''),
      );
      return ok(sorted as unknown as T);
    }
    if (method === 'POST') {
      const body = parseBody<Record<string, unknown>>(options);
      if (!body) return fail('Body requis', 400);

      if (
        body.source === 'AUTO_FROM_TIMESHEET' &&
        typeof body.timesheetId === 'string' &&
        typeof body.consultantName === 'string' &&
        typeof body.companyName === 'string' &&
        typeof body.period === 'string' &&
        typeof body.tjm === 'number' &&
        typeof body.days === 'number'
      ) {
        const p = {
          timesheetId: body.timesheetId,
          consultantName: body.consultantName,
          companyName: body.companyName,
          period: body.period,
          tjm: body.tjm,
          days: body.days,
        };
        if (
          mockInvoicesDB.some(
            i =>
              (i.meta as any)?.timesheetId === p.timesheetId &&
              i.source === InvoiceSource.AUTO_FROM_TIMESHEET,
          )
        ) {
          const existing = mockInvoicesDB.find(i => (i.meta as any)?.timesheetId === p.timesheetId);
          return ok((existing ?? mockInvoicesDB[0]) as unknown as T);
        }
        const { lines, subtotal, vatAmount, total } = computeFromLines([
          {
            label: `Prestation ${p.period} — ${p.consultantName}`,
            quantity: p.days,
            unitPrice: p.tjm,
            vatRate: 0.2,
          },
        ]);
        const now = new Date().toISOString();
        const inv: Invoice = {
          id: `INV_${Math.random().toString(36).slice(2)}`,
          companyName: p.companyName,
          status: InvoiceStatus.DRAFT,
          source: InvoiceSource.AUTO_FROM_TIMESHEET,
          currency: 'EUR',
          period: p.period,
          number: getNextConfirmedInvoiceNumber(mockInvoicesDB),
          lines,
          subtotal,
          vatAmount,
          total,
          meta: { timesheetId: p.timesheetId, consultantName: p.consultantName },
          createdAt: now,
          updatedAt: now,
        };
        mockInvoicesDB.unshift(inv);
        return ok(inv as unknown as T);
      }

      const p = body as {
        companyName: string;
        companyEmail?: string;
        companyAddress?: string;
        paymentDelayDays?: number;
        paymentDelay?: 'upon_receipt' | 'custom_date' | '45_fin_mois';
        purchaseOrder?: string;
        number?: string;
        issuedDate?: string;
        dueDate?: string;
        discount?: { type: 'percentage' | 'amount'; value: number };
        lines: { label: string; quantity: number; unitPrice: number; vatRate: number }[];
      };
      if (!p.companyName?.trim() || !p.lines?.length) {
        return fail('companyName et lines sont requis', 400);
      }
      const { lines, subtotal, vatAmount, total, discount } = computeFromLines(p.lines, p.discount);
      let invoiceNumber: string;
      if (p.number?.trim()) {
        const trimmed = p.number.trim();
        if (!isValidInvoiceNumber(trimmed)) {
          return fail(
            'Le numéro doit être au format FAC-AAAA-00001 (ex: FAC-2025-00001) ou FAC-0002 (ex: FAC-0002)',
            400,
          );
        }
        if (isInvoiceNumberUsedByConfirmedInvoices(mockInvoicesDB, trimmed)) {
          return fail('Le numéro de facture est déjà utilisé', 400);
        }
        invoiceNumber = trimmed;
      } else {
        invoiceNumber = getNextConfirmedInvoiceNumber(mockInvoicesDB);
      }
      const now = new Date().toISOString();
      const inv: Invoice = {
        id: `INV_${Math.random().toString(36).slice(2)}`,
        companyName: p.companyName.trim(),
        companyEmail: p.companyEmail?.trim() || undefined,
        status: InvoiceStatus.DRAFT,
        source: InvoiceSource.BILLING_FORM,
        currency: 'EUR',
        number: invoiceNumber,
        ...(p.issuedDate && {
          issuedAt: p.issuedDate.includes('T') ? p.issuedDate : `${p.issuedDate}T12:00:00.000Z`,
        }),
        lines,
        subtotal,
        ...(discount && { discount }),
        vatAmount,
        total,
        meta: {
          ...(p.paymentDelayDays != null && { paymentDelayDays: p.paymentDelayDays }),
          ...(p.paymentDelay && { paymentDelay: p.paymentDelay }),
          ...(p.dueDate && {
            dueDate: p.dueDate.includes('T') ? p.dueDate : `${p.dueDate}T12:00:00.000Z`,
          }),
          ...(p.companyAddress && { companyAddress: p.companyAddress }),
          ...(p.purchaseOrder && { purchaseOrder: p.purchaseOrder.trim() }),
        },
        createdAt: now,
        updatedAt: now,
      };
      mockInvoicesDB.unshift(inv);
      return ok(inv as unknown as T);
    }
  }

  if (method === 'DELETE' && path.startsWith('/api/v1/admin/invoices/')) {
    const id = path.slice('/api/v1/admin/invoices/'.length);
    const idx = mockInvoicesDB.findIndex(i => i.id === id);
    if (idx === -1) return fail('Facture introuvable', 404);
    const inv = mockInvoicesDB[idx];
    if (inv.status !== InvoiceStatus.DRAFT) {
      return fail('Seules les factures brouillon peuvent être supprimées', 400);
    }
    mockInvoicesDB.splice(idx, 1);
    return ok({ success: true } as unknown as T);
  }

  if (method === 'PATCH' && path.startsWith('/api/v1/admin/invoices/')) {
    const id = path.slice('/api/v1/admin/invoices/'.length);
    const body = parseBody<
      | { action?: 'approve' | 'send'; number?: string }
      | {
          companyName: string;
          companyEmail?: string;
          companyAddress?: string;
          paymentDelayDays?: number;
          lines: { label: string; quantity: number; unitPrice: number; vatRate: number }[];
        }
    >(options);
    const idx = mockInvoicesDB.findIndex(i => i.id === id);
    if (idx === -1) return fail('Facture introuvable', 404);

    const inv = mockInvoicesDB[idx];
    if (
      inv.status === InvoiceStatus.DRAFT &&
      body &&
      typeof (body as Record<string, unknown>).companyName === 'string' &&
      Array.isArray((body as Record<string, unknown>).lines)
    ) {
      const p = body as {
        companyName: string;
        companyEmail?: string;
        companyAddress?: string;
        paymentDelayDays?: number;
        paymentDelay?: 'upon_receipt' | 'custom_date' | '45_fin_mois';
        purchaseOrder?: string;
        number?: string;
        issuedDate?: string;
        dueDate?: string;
        discount?: { type: 'percentage' | 'amount'; value: number } | null;
        lines: { label: string; quantity: number; unitPrice: number; vatRate: number }[];
      };
      const { lines, subtotal, vatAmount, total, discount } = computeFromLines(
        p.lines,
        p.discount ?? undefined,
      );
      let invoiceNumber = inv.number ?? getNextConfirmedInvoiceNumber(mockInvoicesDB);
      if (p.number != null) {
        const trimmed = p.number.trim();
        if (trimmed) {
          if (!isValidInvoiceNumber(trimmed)) {
            return fail(
              'Le numéro doit être au format FAC-AAAA-00001 (ex: FAC-2025-00001) ou FAC-0002 (ex: FAC-0002)',
              400,
            );
          }
          if (isInvoiceNumberUsedByConfirmedInvoices(mockInvoicesDB, trimmed, inv.id)) {
            return fail('Le numéro de facture est déjà utilisé', 400);
          }
          invoiceNumber = trimmed;
        } else {
          invoiceNumber = getNextConfirmedInvoiceNumber(mockInvoicesDB);
        }
      }
      const updatedAt = new Date().toISOString();
      mockInvoicesDB[idx] = {
        ...inv,
        companyName: p.companyName.trim(),
        companyEmail: p.companyEmail?.trim() || undefined,
        number: invoiceNumber,
        ...(p.issuedDate && {
          issuedAt: p.issuedDate.includes('T') ? p.issuedDate : `${p.issuedDate}T12:00:00.000Z`,
        }),
        lines,
        subtotal,
        discount,
        vatAmount,
        total,
        updatedAt,
        meta: {
          ...inv.meta,
          ...(p.paymentDelayDays != null && { paymentDelayDays: p.paymentDelayDays }),
          ...(p.paymentDelay != null && { paymentDelay: p.paymentDelay }),
          ...(p.dueDate != null && {
            dueDate: p.dueDate.includes('T') ? p.dueDate : `${p.dueDate}T12:00:00.000Z`,
          }),
          ...(p.companyAddress != null && { companyAddress: p.companyAddress }),
          ...(p.purchaseOrder != null && { purchaseOrder: p.purchaseOrder.trim() || undefined }),
        },
      };
      return ok(mockInvoicesDB[idx] as unknown as T);
    }

    if ((body as Record<string, unknown>)?.action === 'approve') {
      const inv = mockInvoicesDB[idx];
      if (!canApproveInvoice(inv)) {
        return fail(
          "Données incomplètes pour approuver (client, lignes valides et date d'émission requis)",
          400,
        );
      }
      const bodyNumber = (body as { number?: string }).number?.trim();
      const numberToUse = (() => {
        // Si l'utilisateur a fourni un numéro, on doit le respecter s'il est valide et unique.
        if (bodyNumber) {
          if (!isValidInvoiceNumber(bodyNumber)) {
            return fail(
              'Le numéro doit être au format FAC-AAAA-00001 (ex: FAC-2025-00001) ou FAC-0002 (ex: FAC-0002)',
              400,
            );
          }
          if (isInvoiceNumberUsedByConfirmedInvoices(mockInvoicesDB, bodyNumber, inv.id)) {
            return fail('Le numéro de facture est déjà utilisé', 400);
          }
          return bodyNumber;
        }

        // Sinon on garde le numéro existant (si valide + unique) ou on incrémente.
        const existing = inv.number?.trim();
        if (existing && isValidInvoiceNumber(existing)) {
          if (!isInvoiceNumberUsedByConfirmedInvoices(mockInvoicesDB, existing, inv.id)) {
            return existing;
          }
        }
        return getNextConfirmedInvoiceNumber(mockInvoicesDB);
      })();

      // Canonicalisation : on formate toujours en FAC-AAAA-00001
      const approvedParsed = parseInvoiceNumber(numberToUse);
      const approvedYear = approvedParsed?.year ?? new Date().getFullYear();
      const approvedSeq = approvedParsed?.seq ?? 0;
      const canonicalApprovedNumber = `FAC-${approvedYear}-${String(approvedSeq).padStart(5, '0')}`;

      const isSameSeqYear = (n?: string) => {
        const parsed = n ? parseInvoiceNumber(n) : null;
        if (!parsed) return false;
        const y = parsed.year ?? new Date().getFullYear();
        return y === approvedYear && parsed.seq === approvedSeq;
      };

      // Cas demandé : s'il existe plusieurs factures en brouillon avec le même numéro,
      // basculer les autres sur les numéros suivants après validation.
      const now = new Date().toISOString();
      const draftConflicts = mockInvoicesDB
        .filter(i => i.status === InvoiceStatus.DRAFT && i.id !== inv.id && isSameSeqYear(i.number))
        .sort((a, b) => {
          const ta = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime();
          const tb = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime();
          return ta - tb;
        });

      const usedNumbers = new Set<string>();
      usedNumbers.add(canonicalApprovedNumber.toUpperCase());

      const isInvoiceNumberUsedByAnyInvoice = (candidate: string, excludeId?: string) => {
        const parsedCandidate = parseInvoiceNumber(candidate);
        if (!parsedCandidate) return false;

        return mockInvoicesDB.some(i => {
          if (excludeId && i.id === excludeId) return false;
          if (!i.number?.trim()) return false;
          const parsedExisting = parseInvoiceNumber(i.number);
          if (!parsedExisting) return false;
          const existingYear = parsedExisting.year ?? new Date().getFullYear();
          const candidateYear = parsedCandidate.year ?? new Date().getFullYear();
          return existingYear === candidateYear && parsedExisting.seq === parsedCandidate.seq;
        });
      };

      let nextSeq = approvedSeq + 1;
      const shiftedNumbersById = new Map<string, string>();

      for (const conflict of draftConflicts) {
        let candidateSeq = nextSeq;
        let candidate = `FAC-${approvedYear}-${String(candidateSeq).padStart(5, '0')}`;

        while (
          usedNumbers.has(candidate.toUpperCase()) ||
          isInvoiceNumberUsedByAnyInvoice(candidate, conflict.id)
        ) {
          candidateSeq += 1;
          candidate = `FAC-${approvedYear}-${String(candidateSeq).padStart(5, '0')}`;
        }

        shiftedNumbersById.set(conflict.id, candidate);
        usedNumbers.add(candidate.toUpperCase());
        nextSeq = candidateSeq + 1;
      }

      const mockDraftPdf = 'https://www.africau.edu/images/default/sample.pdf';
      mockInvoicesDB[idx] = {
        ...inv,
        status: InvoiceStatus.INVOICED,
        number: canonicalApprovedNumber,
        updatedAt: now,
        pdfUrl: mockDraftPdf,
      };

      // Mettre à jour les brouillons conflictuels
      if (shiftedNumbersById.size > 0) {
        for (const [conflictId, newNumber] of shiftedNumbersById.entries()) {
          const cIdx = mockInvoicesDB.findIndex(i => i.id === conflictId);
          if (cIdx === -1) continue;
          mockInvoicesDB[cIdx] = {
            ...mockInvoicesDB[cIdx],
            number: newNumber,
            updatedAt: now,
          };
        }
      }
      return ok(mockInvoicesDB[idx] as unknown as T);
    }
    if ((body as Record<string, unknown>)?.action === 'send') {
      const inv = mockInvoicesDB[idx];
      if (inv.status !== InvoiceStatus.INVOICED) {
        return fail('La facture doit être facturée avant envoi', 400);
      }
      // Simule l'URL S3 du PDF après envoi
      const mockPdfUrl = 'https://www.africau.edu/images/default/sample.pdf';
      mockInvoicesDB[idx] = {
        ...inv,
        status: InvoiceStatus.SENT,
        number: inv.number ?? '',
        issuedAt: new Date().toISOString(),
        pdfUrl: mockPdfUrl,
        updatedAt: new Date().toISOString(),
      };
      return ok(mockInvoicesDB[idx] as unknown as T);
    }
    return fail('action invalide (approve | send) ou facture non modifiable', 400);
  }

  /* -------------------------- MISSIONS (CRUD + paginé) -------------------------- */
  if (path === '/api/v1/admin/missions') {
    // GET /api/v1/admin/missions[?page=&limit=]
    if (method === 'GET') {
      const sorted = [...mockMissionsDB].sort(
        (a, b) => new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime(),
      );

      const page = toInt(search.get('page'), 1);
      const limit = toInt(search.get('limit'), 20);
      const hasPaginationParams = search.has('page') || search.has('limit');

      if (!hasPaginationParams) {
        // Mode legacy: retourne la liste complète (sans pagination)
        return ok(sorted as unknown as T);
      }

      const payload = paginate(sorted, page, limit);
      return ok(payload as unknown as T);
    }

    // POST /api/v1/admin/missions
    if (method === 'POST') {
      const body = parseBody<Partial<Mission>>(options);

      // Champs obligatoires : porté, client, titre, date de début, type d’honoraires et taux
      // Même pour les brouillons, les champs obligatoires doivent être remplis.
      if (
        !body?.consultantId ||
        !body?.clientId ||
        !body?.title ||
        !body?.startDate ||
        !body?.feeType ||
        body?.rate == null ||
        body?.rate <= 0
      ) {
        return fail('Veuillez remplir tous les champs obligatoires', 400);
      }

      const resolvedClientName =
        (body as any).clientName ??
        mockClientsDB.find(c => c.id === String(body.clientId))?.name ??
        undefined;

      const now = new Date().toISOString();
      const orderFormUrl = (body as any).orderFormUrl as string | undefined;
      const orderFormFileName = (body as any).orderFormFileName as string | undefined;
      const orderForms: MissionOrderForm[] =
        orderFormUrl && orderFormFileName
          ? [
              {
                id: `of-${Date.now()}`,
                name: orderFormFileName,
                url: orderFormUrl,
                uploadedAt: now,
              },
            ]
          : [];

      const created: Mission = {
        id: String(Date.now()),
        title: body?.title ? String(body.title) : '',
        consultantId: body?.consultantId ? String(body.consultantId) : '',
        consultantName: (body as any).consultantName,
        clientId: body?.clientId ? String(body.clientId) : '',
        clientName: resolvedClientName,
        startDate: body?.startDate ? String(body.startDate) : '',
        endDate: body.endDate ? String(body.endDate) : undefined,
        estimatedEndDate: body.estimatedEndDate ? String(body.estimatedEndDate) : undefined,
        feeType: (body.feeType as FeeType) ?? FeeType.DAY,
        rate: body?.rate == null ? 0 : Number(body.rate),
        initialDuration:
          body.initialDuration == null ? undefined : Number(body.initialDuration ?? undefined),
        paymentDelay:
          body.paymentDelay == null ? undefined : Number(body.paymentDelay ?? undefined),
        currency: (body.currency as Currency) ?? Currency.EUR,
        bankAccountId: body.bankAccountId ? String(body.bankAccountId) : undefined,
        bankAccountName: (body as any).bankAccountName,
        usesTimesheet: Boolean(body.usesTimesheet),
        usesFixedMilestones: Boolean(body.usesFixedMilestones),
        tacitRenewal: Boolean(body.tacitRenewal),
        status: (body.status as MissionStatus) ?? MissionStatus.ACTIVE,
        location: body.location ? String(body.location) : undefined,
        clientManager: (body as any).clientManager,
        isTrainingMission: Boolean((body as any).isTrainingMission),
        specificManagementFeeRate: Boolean((body as any).specificManagementFeeRate),
        timesheetValidatedByClient: Boolean((body as any).timesheetValidatedByClient),
        refundableNdf: Boolean((body as any).refundableNdf),
        timesheetJustificationRequired: Boolean((body as any).timesheetJustificationRequired),
        generateTimesheetPdfAutomatically: Boolean((body as any).generateTimesheetPdfAutomatically),
        internalReference: (body as any).internalReference,
        contractNumber: (body as any).contractNumber,
        createdBy: (body as any).createdBy,
        createdAt: now,
        updatedAt: now,
        orderForms: orderForms.length > 0 ? orderForms : undefined,
      };

      mockMissionsDB.unshift(created);
      return ok(created as unknown as T);
    }
  }

  // PATCH /api/v1/admin/missions/:id (mise à jour simple, ex: compléter un brouillon)
  if (method === 'PATCH' && path.startsWith('/api/v1/admin/missions/')) {
    const missionId = path.split('/').pop();
    if (!missionId) return fail('Mission introuvable', 404);

    const body =
      parseBody<Partial<Mission> & { orderFormUrl?: string; orderFormFileName?: string }>(
        options,
      ) ?? {};
    const idx = mockMissionsDB.findIndex(m => m.id === missionId);
    if (idx < 0) return fail('Mission introuvable', 404);

    const nextStatus = (body.status as MissionStatus | undefined) ?? mockMissionsDB[idx].status;

    // Même pour les brouillons, les champs obligatoires doivent être remplis
    const candidate = { ...mockMissionsDB[idx], ...body } as Mission;
    if (
      !candidate.consultantId ||
      !candidate.clientId ||
      !candidate.title ||
      !candidate.startDate ||
      !candidate.feeType ||
      candidate.rate == null ||
      candidate.rate <= 0
    ) {
      return fail('Veuillez remplir tous les champs obligatoires', 400);
    }

    const existingOrderForms = mockMissionsDB[idx].orderForms ?? [];
    const newOrderForm: MissionOrderForm | null =
      body.orderFormUrl && body.orderFormFileName
        ? {
            id: `of-${Date.now()}`,
            name: body.orderFormFileName,
            url: body.orderFormUrl,
            uploadedAt: new Date().toISOString(),
          }
        : null;
    const orderForms =
      newOrderForm !== null ? [...existingOrderForms, newOrderForm] : existingOrderForms;

    const updated: Mission = {
      ...mockMissionsDB[idx],
      ...body,
      consultantId:
        body.consultantId == null ? mockMissionsDB[idx].consultantId : String(body.consultantId),
      clientId: body.clientId == null ? mockMissionsDB[idx].clientId : String(body.clientId),
      title: body.title == null ? mockMissionsDB[idx].title : String(body.title),
      startDate: body.startDate == null ? mockMissionsDB[idx].startDate : String(body.startDate),
      endDate: body.endDate == null ? mockMissionsDB[idx].endDate : String(body.endDate),
      estimatedEndDate:
        body.estimatedEndDate == null
          ? mockMissionsDB[idx].estimatedEndDate
          : String(body.estimatedEndDate),
      rate: body.rate == null ? mockMissionsDB[idx].rate : Number(body.rate),
      location: body.location != null ? String(body.location) : mockMissionsDB[idx].location,
      createdBy:
        (body as any).createdBy != null ? (body as any).createdBy : mockMissionsDB[idx].createdBy,
      updatedAt: new Date().toISOString(),
      status: nextStatus,
      orderForms: orderForms.length > 0 ? orderForms : mockMissionsDB[idx].orderForms,
    };

    mockMissionsDB[idx] = updated;
    return ok(updated as unknown as T);
  }

  // DELETE /api/v1/admin/missions/:id
  if (method === 'DELETE' && path.startsWith('/api/v1/admin/missions/')) {
    const missionId = path.split('/').pop();
    if (!missionId) return fail('Mission introuvable', 404);
    const idx = mockMissionsDB.findIndex(m => m.id === missionId);
    if (idx < 0) return fail('Mission introuvable', 404);
    mockMissionsDB.splice(idx, 1);
    return ok({ success: true } as unknown as T);
  }

  /* -------------------- EXPENSE TYPES (GET /expenses/types) -------------------- */
  if (method === 'GET' && path === '/api/v1/expenses/types') {
    const isInternal = (mockUser.profileType ?? 'CONSULTANT') === 'INTERNAL';
    const internalTypes = [
      {
        id: 'et-transport',
        value: 'transport',
        label: 'Transport',
        isMileage: false,
        requiresReceipt: true,
      },
      { id: 'et-repas', value: 'repas', label: 'Repas', isMileage: false, requiresReceipt: true },
      {
        id: 'et-fournitures',
        value: 'fournitures',
        label: 'Fournitures',
        isMileage: false,
        requiresReceipt: true,
      },
    ];
    const consultantTypes = [
      ...internalTypes,
      {
        id: 'et-hebergement',
        value: 'hebergement',
        label: 'Hébergement',
        isMileage: false,
        requiresReceipt: true,
      },
      {
        id: 'et-it',
        value: 'equipement_it',
        label: 'Équipement IT',
        isMileage: false,
        requiresReceipt: true,
      },
      {
        id: 'et-ik',
        value: 'ik',
        label: 'Indemnités kilométriques',
        isMileage: true,
        requiresReceipt: false,
      },
    ];
    return ok((isInternal ? internalTypes : consultantTypes) as unknown as T);
  }

  /* -------------------- EXPENSES (paginated) -------------------- */
  /* -------------------- EXPENSES (CRUD + paginated) -------------------- */
  if (path === '/api/v1/expenses') {
    // GET /api/v1/expenses?page=1&limit=&year=&month=&status=&sort=
    if (method === 'GET') {
      const page = toInt(search.get('page'), 1);
      const limit = toInt(search.get('limit'), 20);
      const year = search.get('year');
      const month = search.get('month');
      const status = toStatus(search.get('status'));
      const sort = search.get('sort'); // 'asc' | 'desc' (par (year,month))

      let items = [...mockExpensesDB];

      // filtre par année/mois si fournis
      if (year) items = items.filter(e => String(e.year) === String(year));
      if (month) items = items.filter(e => String(e.month) === String(month));

      // filtre par status de l'ENVELOPPE (expensesStatus) si fourni
      if (status) items = items.filter(e => e.expensesStatus === status);

      // tri par (year, month)
      items = sortMonthEntries(items, sort);

      const payload = paginate(items, page, limit);
      return ok(payload as unknown as T);
    }

    // POST /api/v1/expenses  (ajouter UNE dépense dans un mois)
    // body: { date: string(YYYY-MM-DD), type, description, amount:number, receiptUrl?:string }
    if (method === 'POST') {
      const body = parseBody<{
        date?: string;
        type?: string;
        description?: string;
        amount?: number | string;
        receiptUrl?: string;
      }>(options);

      if (!body?.date || !body?.type || !body?.description || body?.amount == null) {
        return fail('Champs requis: date, type, description, amount', 400);
      }

      const d = new Date(body.date);
      if (isNaN(d.getTime())) return fail('Date invalide', 400);

      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const amountNum = typeof body.amount === 'string' ? Number(body.amount) : body.amount;
      if (!Number.isFinite(amountNum)) return fail('Montant invalide', 400);

      const newItem = {
        id: Date.now(),
        date: body.date,
        type: body.type!,
        description: body.description!,
        amount: amountNum!,
        status: ExpenseStatus.CREATED,
        receiptUrl: body.receiptUrl ?? '/receipts/placeholder.pdf',
      };

      const idx = mockExpensesDB.findIndex(e => e.year === y && e.month === m);
      if (idx >= 0) {
        mockExpensesDB[idx] = {
          ...mockExpensesDB[idx],
          expenses: [...mockExpensesDB[idx].expenses, newItem],
          // Si le mois était validé, on ne change pas; sinon on garantit au moins CREATED
          expensesStatus:
            mockExpensesDB[idx].expensesStatus === ExpenseStatus.VALIDATED
              ? ExpenseStatus.VALIDATED
              : ExpenseStatus.CREATED,
        };
      } else {
        mockExpensesDB.push({
          id: Date.now(),
          userId: 0,
          year: y,
          month: m,
          expensesStatus: ExpenseStatus.CREATED,
          expenses: [newItem],
        });
      }

      return ok(newItem as unknown as T);
    }

    // PATCH /api/v1/expenses/submit?year=&month=
    // -> passe le mois en PENDING et convertit CREATED -> PENDING
    if (method === 'PATCH' && search.get('action') === 'submit') {
      const year = toInt(search.get('year'), NaN);
      const month = toInt(search.get('month'), NaN);
      if (!Number.isFinite(year) || !Number.isFinite(month))
        return fail('year & month requis', 400);

      const idx = mockExpensesDB.findIndex(e => e.year === year && e.month === month);
      if (idx < 0) return fail('Mois introuvable', 404);

      const entry = mockExpensesDB[idx];
      const updated = {
        ...entry,
        expensesStatus: ExpenseStatus.PENDING,
        expenses: entry.expenses.map(x =>
          x.status === ExpenseStatus.CREATED ? { ...x, status: ExpenseStatus.PENDING } : x,
        ),
      };
      mockExpensesDB[idx] = updated;

      return ok(updated as unknown as T);
    }
  }

  // DELETE /api/v1/expenses/:id?year=&month=
  if (method === 'DELETE' && path.startsWith('/api/v1/expenses/')) {
    const idStr = path.split('/').pop();
    const id = Number(idStr);
    if (!Number.isFinite(id)) return fail('id invalide', 400);

    const year = toInt(new URLSearchParams(search).get('year'), NaN);
    const month = toInt(new URLSearchParams(search).get('month'), NaN);
    if (!Number.isFinite(year) || !Number.isFinite(month)) return fail('year & month requis', 400);

    const idx = mockExpensesDB.findIndex(e => e.year === year && e.month === month);
    if (idx < 0) return fail('Mois introuvable', 404);

    const before = mockExpensesDB[idx].expenses.length;
    mockExpensesDB[idx].expenses = mockExpensesDB[idx].expenses.filter(x => x.id !== id);
    const after = mockExpensesDB[idx].expenses.length;

    if (before === after) return fail('Dépense introuvable', 404);
    return ok({ ok: true } as unknown as T);
  }

  /* -------------------- PROFILE ONBOARDING -------------------- */
  if (path === '/api/v1/profile/onboarding') {
    if (method === 'GET') {
      return ok({
        completed: true,
        missingFields: [],
        profile: null,
      } as unknown as T);
    }
    if (method === 'PATCH') {
      return ok({
        completed: true,
        missingFields: [],
        profile: parseBody(options) ?? {},
      } as unknown as T);
    }
  }

  /* -------------------- DOCUMENTS (full list, no pagination) -------------------- */
  if (method === 'GET' && path === '/api/v1/documents') {
    const sorted = [...mockDocuments].sort(
      (a, b) => new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime(),
    );
    return ok(sorted as unknown as T);
  }

  /* -------------------- USER DETAILS (non paginé) -------------------- */
  if (method === 'GET' && path.startsWith('/api/v1/users/')) {
    const userId = path.split('/').pop()!;
    if (mockUser.id === userId || userId === 'user_123') {
      const details = {
        id: userId,
        email: mockUser.email,
        fullName: mockUser.fullName,
        firstName: mockUser.firstName,
        lastName: mockUser.lastName,
        avatarUrl: mockUser.avatarUrl,
        companyName: mockUser.companyName,
        roles: mockUser.roles,
        manager: { id: 'manager_1', fullName: 'Jane Manager', email: 'jane.manager@example.com' },
        phone: '+33 6 12 34 56 78',
      };
      return ok(details as unknown as T);
    }
    return fail('User not found', 404);
  }

  /* -------------------- HR INBOX (admin) -------------------- */
  if (method === 'GET' && path === '/api/v1/admin/hr-inbox/timesheets/pending') {
    const page = toInt(search.get('page'), 1);
    const limit = toInt(search.get('limit'), 200);
    return ok(paginate<unknown>([], page, limit) as unknown as T);
  }

  if (method === 'GET' && path === '/api/v1/admin/hr-inbox/expenses/pending') {
    const page = toInt(search.get('page'), 1);
    const limit = toInt(search.get('limit'), 200);
    return ok(paginate<unknown>([], page, limit) as unknown as T);
  }

  const hrTimesheetDetail = path.match(
    /^\/api\/v1\/admin\/hr-inbox\/timesheets\/([^/]+)\/(\d+)\/(\d+)$/,
  );
  if (method === 'GET' && hrTimesheetDetail) {
    const year = Number(hrTimesheetDetail[2]);
    const month = Number(hrTimesheetDetail[3]);
    return ok({
      year,
      month,
      timesheetStatus: 'SUBMITTED',
      comment: '',
      activities: [],
    } as unknown as T);
  }

  if (
    method === 'PATCH' &&
    /^\/api\/v1\/admin\/hr-inbox\/timesheets\/[^/]+\/\d+\/\d+$/.test(path)
  ) {
    return ok({ timesheetStatus: 'VALIDATED' } as unknown as T);
  }

  const hrExpenseEnvelope = path.match(
    /^\/api\/v1\/admin\/hr-inbox\/expenses\/([^/]+)\/(\d+)\/(\d+)$/,
  );
  if (method === 'GET' && hrExpenseEnvelope) {
    const year = Number(hrExpenseEnvelope[2]);
    const month = Number(hrExpenseEnvelope[3]);
    return ok({
      year,
      month,
      expensesStatus: 'PENDING',
      expenses: [],
    } as unknown as T);
  }

  if (method === 'PATCH' && path.startsWith('/api/v1/admin/hr-inbox/expenses/lines/')) {
    return ok({ ok: true } as unknown as T);
  }

  if (method === 'POST' && path === '/api/v1/admin/hr-inbox/expenses/lines/bulk-approve') {
    return ok({ updated: 0 } as unknown as T);
  }

  if (method === 'GET' && path === '/api/v1/admin/integrations/payroll/status') {
    return ok({
      configured: false,
      linkedConsultants: 0,
      storedPayslips: 0,
      storedAbsences: 0,
      unmatchedPayfitCollaborators: 0,
    } as unknown as T);
  }

  if (method === 'POST' && path === '/api/v1/admin/integrations/payroll/sync') {
    return ok({
      collaboratorsTotal: 0,
      collaboratorsMatched: 0,
      collaboratorsUnmatched: 0,
      payslipsSynced: 0,
      payslipsSkipped: 0,
      absencesSynced: 0,
      absencesSkipped: 0,
      unmatched: [],
      errors: [],
    } as unknown as T);
  }

  if (method === 'POST' && path === '/api/v1/admin/integrations/payroll/link') {
    return ok({ ok: true } as unknown as T);
  }

  /* -------------------------- FALLBACK -------------------------- */
  return fail(`[mockHttpClient] Endpoint non mocké: ${method} ${url}`, 404);
}
